import { getCommaOut } from "../../../../../utils/hooks/dataHandler";

export const hasNetDisposalConsideration = (row) =>
  row.netDisposalConsideration !== undefined &&
  row.netDisposalConsideration !== null &&
  String(row.netDisposalConsideration).trim() !== "";

// Existing saved amounts imply an enabled row until it has an explicit toggle.
export const isNetDisposalEnabled = (row) =>
  row.netDisposalConsiderationEnabled ?? hasNetDisposalConsideration(row);

export const getS19DClaimedBf = (row) => {
  if (!row.s19dWdaBasis) {
    const cost = Number(getCommaOut(row.cost));
    const opening = Number(getCommaOut(row.twdvBf));
    if (Number.isFinite(cost) && cost > 0 && cost >= opening) return cost - opening;
  }
  if (row.s19dWdaClaimedBf != null && Number.isFinite(Number(row.s19dWdaClaimedBf)) && Number(row.s19dWdaClaimedBf) >= 0)
    return Number(row.s19dWdaClaimedBf);
  return null;
};

export const getNetDisposalError = (row) => {
  if (!isNetDisposalEnabled(row)) return "";
  if (!hasNetDisposalConsideration(row)) return "Enter a disposal amount.";
  const consideration = Number(getCommaOut(row.netDisposalConsideration));
  const opening = Number(getCommaOut(row.twdvBf));
  const years = Number(row.noOfYearClaimsBf);
  if (!Number.isFinite(consideration) || consideration < 0)
    return "Enter a net consideration of zero or more.";
  if (!Number.isFinite(opening) || opening < 0)
    return "Enter a valid TWDV b/f.";
  if (consideration > opening && getS19DClaimedBf(row) === null)
    return "Enter the original Cost or confirm the cumulative WDA previously claimed before calculating BC.";
  if (opening > consideration && (!Number.isInteger(years) || years <= 0))
    return "Enter the complete remaining years in No. of years b/f.";
  if (row.source?.assets?.length > 1 || row.hpAssets?.length > 1)
    return "Grouped source rows are not supported in this mode yet.";
  return "";
};

// The row toggle selects partial disposal; zero is a valid entered amount.
export const applyNetDisposalConsideration = (row, yearOfAssessment) => {
  if (!isNetDisposalEnabled(row)) {
    const basis = row.s19dWdaBasis;
    return basis && Number(basis.remainingYears) > 0
      ? { amount: Number(basis.adjustedBasis), years: Number(basis.remainingYears) }
      : null;
  }
  if (getNetDisposalError(row)) {
    delete row.s19dDisposalAdjustment;
    return { amount: 0, years: 0 };
  }
  const consideration = Number(getCommaOut(row.netDisposalConsideration));
  const opening = Number(getCommaOut(row.twdvBf));
  const years = Number(row.noOfYearClaimsBf);
  const claimedBf = getS19DClaimedBf(row);
  row.s19dOriginalDisposalAmount ??= row.currentAmount || 0;
  row.netDisposalConsideration = consideration;
  // Net consideration replaces the ordinary disposal TWDV deduction.
  row.currentAmount = consideration === 0 ? 0 : -consideration;
  row.totalAmount = Math.max(0, opening - consideration);
  row.s19dWdaClaimedBf = claimedBf;
  row.s19dDisposalAdjustment = {
    yearOfAssessment: Number(yearOfAssessment),
    unallowedExpenditure: opening,
    consideration,
    remainingYears: years,
    adjustedBasis: row.totalAmount,
    balancingCharge: consideration > opening
      ? Math.min(consideration - opening, Math.max(0, claimedBf - (Number(row.s19dBcChargedBf) || 0))) : 0,
  };
  return { amount: row.totalAmount, years: opening > consideration ? years : 0 };
};

export const getS19DDisposalClaim = (detail, claims = []) => {
  const sourceId = String(detail.registeredAssetSourceId || "").replace(/^fa-register-disposal-/, "");
  const matches = claims.filter((claim) => {
    if ((!isNetDisposalEnabled(claim) && !claim.s19dWdaBasis) || claim.caName !== detail.CaName ||
      Number(claim.ya) !== Number(detail.yaOfPurchase) ||
      Number(claim.noOfYearClaimsBf) !== Number(detail.noOfYearsBf) ||
      Boolean(claim.isHirePurchase) !== Boolean(detail.isHirePurchase)) return false;
    const keys = claim.source?.rowKeys;
    return sourceId ? keys?.map(String).includes(sourceId) : !keys?.length;
  });
  if (matches.length > 1) return { warning: "More than one saved S19D disposal matches this row. Review the CA rows." };
  if (!matches.length) return null;
  const claim = { ...matches[0] };
  if (sourceId && (Number(claim.cost) !== Number(detail.costInDetails) || Number(claim.twdvBf) !== Number(detail.twdvBf)))
    return { warning: "The saved S19D claim no longer matches FA Register. Review and save the CA schedule again." };
  const key = claim.rowId || JSON.stringify([claim.caName, claim.ya, claim.noOfYearClaimsBf, claim.isHirePurchase]);
  if (!isNetDisposalEnabled(claim)) {
    const claimed = getS19DClaimedBf(claim);
    if (claimed === null || Number(detail.twdvBf) !== Number(claim.twdvBf)) return null;
    const remainingCap = Math.max(0, claimed - (Number(claim.s19dBcChargedBf) || 0));
    return { key, bcBa: Math.max(Number(detail.twdvBf) - Number(detail.salesProceeds), -remainingCap) };
  }
  const warning = getNetDisposalError(claim);
  if (warning) return { warning };
  applyNetDisposalConsideration(claim, claim.s19dDisposalAdjustment?.yearOfAssessment);
  return {
    key,
    bcBa: -claim.s19dDisposalAdjustment.balancingCharge || 0,
  };
};
